<template>
  <avs-card class="fidelity-stats-container">
    <!-- ===== HEADER ===== -->
    <template #title>
      <div>
        <v-container fluid style="padding: 0;">
          <!-- Titre + Filtres -->
          <v-row class="mb-4 align-center">
            <v-col cols="12" md="3">
              <div class="d-flex align-center">
                <v-icon large color="primary" class="mr-2">mdi-chart-areaspline</v-icon>
                <div>
                  <h3 class="text-h6 font-weight-bold mb-0">{{ $t('Statistiques Fidélité') }}</h3>
                  <div class="text-caption grey--text">{{ $t('Analyse de consommation') }}</div>
                </div>
              </div>
            </v-col>

            <v-col cols="12" md="2">
              <v-menu v-model="menuStartDate" :close-on-content-click="false" transition="scale-transition" offset-y min-width="230px">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="startDate"
                    :label="$t('Date de début')"
                    prepend-inner-icon="mdi-calendar-start"
                    outlined dense hide-details readonly
                    v-bind="attrs" v-on="on"
                  />
                </template>
                <v-date-picker v-model="startDate" @input="menuStartDate = false; applyFilters()" color="primary" />
              </v-menu>
            </v-col>

            <v-col cols="12" md="2">
              <v-menu v-model="menuEndDate" :close-on-content-click="false" transition="scale-transition" offset-y min-width="230px">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="endDate"
                    :label="$t('Date de fin')"
                    prepend-inner-icon="mdi-calendar-end"
                    outlined dense hide-details readonly
                    v-bind="attrs" v-on="on"
                  />
                </template>
                <v-date-picker v-model="endDate" @input="menuEndDate = false; applyFilters()" color="primary" />
              </v-menu>
            </v-col>

            <v-col cols="12" md="2">
              <v-autocomplete
                v-model="selectedProduct"
                clearable
                :items="products"
                @input="applyFilters"
                item-text="name" item-value="id"
                :label="$t('Produits')"
                outlined dense hide-details
                prepend-inner-icon="mdi-gas-station"
              />
            </v-col>

            <v-col cols="12" md="3" class="d-flex align-center justify-end">
              <v-btn color="success" outlined small class="mr-2" @click="exportExcel" :loading="isExportingExcel">
                <v-icon small left>mdi-file-excel</v-icon> Excel
              </v-btn>
              <v-btn color="error" outlined small class="mr-2" @click="exportPdf" :loading="isExportingPdf">
                <v-icon small left>mdi-file-pdf-box</v-icon> PDF
              </v-btn>
              <v-btn small outlined color="primary" @click="applyFilters" :loading="isLoading">
                <v-icon left>mdi-refresh</v-icon>{{ $t('Actualiser') }}
              </v-btn>
            </v-col>
          </v-row>

          <!-- KPIs Section -->
          <v-row class="stats-section" v-if="stats">
            <v-col cols="12">
              <div class="stats-container">
                <!-- Total utilisateurs -->
                <v-card class="stat-card stat-card-main" elevation="4">
                  <div class="d-flex align-center justify-space-between">
                    <div>
                      <div class="stat-label">{{ $t('Utilisateurs Fidélité') }}</div>
                      <div class="stat-value">{{ formatNumber(stats.users.total) }}</div>
                    </div>
                    <div class="stat-icon">
                      <v-icon size="40" color="white">mdi-account-group</v-icon>
                    </div>
                  </div>
                  <v-divider class="my-0" style="border-color: rgba(255,255,255,0.3);" />
                  <div class="stat-secondary mt-2">
                    <v-icon small color="white">mdi-cellphone</v-icon>
                    {{ formatNumber(stats.users.with_app) }} {{ $t('avec app') }}
                    <span class="mx-1">·</span>
                    <v-icon small color="white">mdi-credit-card-outline</v-icon>
                    {{ formatNumber(stats.users.without_app) }} {{ $t('carte physique') }}
                  </div>
                </v-card>

                <!-- Actifs 30j -->
                <v-card class="stat-card stat-card-product bgTable" elevation="3">
                  <div class="d-flex align-center justify-space-between">
                    <div>
                      <div class="stat-label">{{ $t('Actifs') }}</div>
                      <div class="stat-value stat-value-success">{{ formatNumber(stats.users.active_count) }}</div>
                    </div>
                    <div class="stat-icon stat-icon-success">
                      <v-icon size="40" color="success">mdi-account-check</v-icon>
                    </div>
                  </div>
                  <div class="stat-footer mt-2">
                    <v-icon x-small>mdi-account-plus</v-icon>
                    {{ formatNumber(stats.users.new_this_month) }} {{ $t('nouveaux ce mois') }}
                  </div>
                </v-card>

                <!-- Total points -->
                <v-card class="stat-card stat-card-product bgTable" elevation="3">
                  <div class="d-flex align-center justify-space-between">
                    <div>
                      <div class="stat-label">{{ $t('Points distribués') }}</div>
                      <div class="stat-value stat-value-primary">{{ formatNumber(stats.users.total_points) }}</div>
                    </div>
                    <div class="stat-icon stat-icon-primary">
                      <v-icon size="40" color="primary">mdi-star-circle</v-icon>
                    </div>
                  </div>
                  <div class="stat-footer mt-2">
                    <v-icon x-small>mdi-star-check</v-icon>
                    {{ formatNumber(stats.users.total_points_redeemed) }} {{ $t('points échangés') }}
                  </div>
                </v-card>

                <!-- Montant encaissé (redemptions) -->
                <v-card class="stat-card stat-card-product bgTable" elevation="3">
                  <div class="d-flex align-center justify-space-between">
                    <div>
                      <div class="stat-label">{{ $t('Montant encaissé') }}</div>
                      <div class="stat-value stat-value-warning">
                        {{ formatNumber(stats.redemptions ? stats.redemptions.fulfilled_amount : 0) }}
                        <span class="stat-unit">XOF</span>
                      </div>
                    </div>
                    <div class="stat-icon stat-icon-warning">
                      <v-icon size="40" color="orange">mdi-cash-multiple</v-icon>
                    </div>
                  </div>
                  <div class="stat-footer mt-2">
                    <v-icon x-small>mdi-cash-check</v-icon>
                    {{ formatNumber(stats.redemptions ? stats.redemptions.approved_amount : 0) }} XOF {{ $t('à encaisser') }}
                  </div>
                </v-card>
              </div>
            </v-col>
          </v-row>

        </v-container>
      </div>
    </template>

    <!-- ===== CONTENU ONGLETS ===== -->
    <template #content>
      <div class="col-12 pa-6">
        <v-skeleton-loader v-if="isLoading && !stats" type="card, table" class="mb-4" />

        <v-tabs v-model="activeTab" background-color="transparent" color="primary" class="mb-6" v-if="stats">
          <v-tab><v-icon left small>mdi-chart-bar</v-icon>{{ $t('Consommation') }}</v-tab>
          <v-tab><v-icon left small>mdi-map-marker</v-icon>{{ $t('Stations') }}</v-tab>
          <v-tab><v-icon left small>mdi-account-multiple</v-icon>{{ $t('Utilisateurs') }}</v-tab>
          <v-tab><v-icon left small>mdi-hand-coin</v-icon>{{ $t('Rédemptions') }}</v-tab>
          <v-tab>
            <v-icon left small>mdi-account-remove</v-icon>{{ $t('Inactifs') }}
            <v-chip x-small color="error" text-color="white" class="ml-2" v-if="stats.users.inactive_30d">{{ stats.users.inactive_30d }}</v-chip>
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="activeTab" v-if="stats">
          <!-- ===== ONGLET 1 : CONSOMMATION ===== -->
          <v-tab-item eager>
            <v-row>
              <!-- Graphique évolution mensuelle -->
              <v-col cols="12" md="8">
                <v-card outlined class="pa-4 rounded-lg">
                  <div class="text-subtitle-1 font-weight-bold mb-4">
                    <v-icon left small color="primary">mdi-chart-timeline-variant</v-icon>
                    {{ $t('Évolution mensuelle') }}
                  </div>
                  <div ref="monthlyChart" style="min-height: 350px;"></div>
                </v-card>
              </v-col>

              <!-- Graphique répartition par produit -->
              <v-col cols="12" md="4">
                <v-card outlined class="pa-4 rounded-lg">
                  <div class="text-subtitle-1 font-weight-bold mb-4">
                    <v-icon left small color="primary">mdi-chart-donut</v-icon>
                    {{ $t('Répartition par produit') }}
                  </div>
                  <div ref="productChart" style="min-height: 350px;"></div>
                </v-card>
              </v-col>

              <!-- Tableau produits -->
              <v-col cols="12">
                <v-card outlined class="rounded-lg">
                  <v-card-title class="text-subtitle-1 font-weight-bold">
                    <v-icon left small color="primary">mdi-gas-station</v-icon>
                    {{ $t('Détails par produit') }}
                  </v-card-title>
                  <v-data-table
                    class="bgTable"
                    dense
                    :headers="productHeaders"
                    :items="stats.consumption.by_product"
                    hide-default-footer
                    :items-per-page="-1"
                    :no-data-text="$t('messages.information.no_data')"
                  >
                    <template v-slot:item.total_amount="{ item }">
                      <span class="font-weight-bold">{{ formatNumber(item.total_amount) }} XOF</span>
                    </template>
                    <template v-slot:item.total_liters="{ item }">
                      {{ formatNumber(item.total_liters) }} L
                    </template>
                    <template v-slot:item.transaction_count="{ item }">
                      <v-chip small label color="accent" text-color="white">
                        {{ formatNumber(item.transaction_count) }}
                      </v-chip>
                    </template>
                  </v-data-table>
                </v-card>
              </v-col>
            </v-row>
          </v-tab-item>

          <!-- ===== ONGLET 2 : STATIONS ===== -->
          <v-tab-item lazy>
            <v-row>
              <!-- Graphique top stations -->
              <v-col cols="12">
                <v-card outlined class="pa-4 rounded-lg">
                  <div class="text-subtitle-1 font-weight-bold mb-4">
                    <v-icon left small color="primary">mdi-chart-bar</v-icon>
                    {{ $t('Top 10 stations par volume') }}
                  </div>
                  <div ref="stationChart" style="min-height: 400px;"></div>
                </v-card>
              </v-col>

              <!-- Tableau stations -->
              <v-col cols="12">
                <v-card outlined class="rounded-lg">
                  <v-card-title class="d-flex align-center justify-space-between">
                    <div class="text-subtitle-1 font-weight-bold">
                      <v-icon left small color="primary">mdi-map-marker-multiple</v-icon>
                      {{ $t('Performance par station') }}
                    </div>
                    <v-btn small outlined color="success" @click="exportStationsExcel">
                      <v-icon small left>mdi-file-excel</v-icon> {{ $t('Exporter') }}
                    </v-btn>
                  </v-card-title>
                  <v-data-table
                    class="bgTable"
                    dense
                    :headers="stationHeaders"
                    :items="stats.consumption.by_station"
                    :items-per-page="10"
                    :no-data-text="$t('messages.information.no_data')"
                  >
                    <template v-slot:item.name="{ item }">
                      <div class="d-flex align-center">
                        <v-icon small color="primary" class="mr-2">mdi-map-marker</v-icon>
                        <span class="font-weight-bold">{{ item.name }}</span>
                      </div>
                    </template>
                    <template v-slot:item.total_amount="{ item }">
                      <span class="font-weight-bold">{{ formatNumber(item.total_amount) }} XOF</span>
                    </template>
                    <template v-slot:item.total_liters="{ item }">
                      {{ formatNumber(item.total_liters) }} L
                    </template>
                    <template v-slot:item.transaction_count="{ item }">
                      <v-chip small label color="accent" text-color="white">
                        {{ formatNumber(item.transaction_count) }}
                      </v-chip>
                    </template>
                    <template v-slot:item.unique_users="{ item }">
                      <v-chip
                        small label outlined color="primary"
                        style="cursor: pointer;"
                        @click="openStationUsers(item)"
                      >
                        <v-icon left x-small>mdi-account</v-icon>
                        {{ formatNumber(item.unique_users) }}
                      </v-chip>
                    </template>
                  </v-data-table>
                </v-card>
              </v-col>
            </v-row>
          </v-tab-item>

          <!-- ===== ONGLET 3 : UTILISATEURS ===== -->
          <v-tab-item lazy>
            <v-row>
              <!-- Graphique répartition avec/sans app -->
              <v-col cols="12" md="4">
                <v-card outlined class="pa-4 rounded-lg">
                  <div class="text-subtitle-1 font-weight-bold mb-4">
                    <v-icon left small color="primary">mdi-cellphone</v-icon>
                    {{ $t('App mobile / Carte physique') }}
                  </div>
                  <div ref="appChart" style="min-height: 300px;"></div>
                </v-card>
              </v-col>

              <!-- Top utilisateurs -->
              <v-col cols="12" md="8">
                <v-card outlined class="rounded-lg">
                  <v-card-title class="d-flex align-center justify-space-between">
                    <div class="text-subtitle-1 font-weight-bold">
                      <v-icon left small color="primary">mdi-podium-gold</v-icon>
                      {{ $t('Top utilisateurs') }}
                    </div>
                    <v-btn small outlined color="success" @click="exportUsersExcel">
                      <v-icon small left>mdi-file-excel</v-icon> {{ $t('Exporter') }}
                    </v-btn>
                  </v-card-title>
                  <v-data-table
                    class="bgTable"
                    dense
                    :headers="userHeaders"
                    :items="stats.consumption.top_users"
                    :items-per-page="10"
                    :no-data-text="$t('messages.information.no_data')"
                  >
                    <template v-slot:item.fullname="{ item }">
                      <div class="d-flex align-center py-2">
                        <v-avatar size="32" color="primary" class="mr-2">
                          <span class="white--text text-caption font-weight-bold">
                            {{ getInitials(item.fullname) }}
                          </span>
                        </v-avatar>
                        <div>
                          <div class="font-weight-bold capitalize">{{ item.fullname || 'N/A' }}</div>
                          <div class="text-caption grey--text">{{ item.phone || 'N/A' }}</div>
                        </div>
                      </div>
                    </template>
                    <template v-slot:item.total_amount="{ item }">
                      <span class="font-weight-bold">{{ formatNumber(item.total_amount) }} XOF</span>
                    </template>
                    <template v-slot:item.total_liters="{ item }">
                      {{ formatNumber(item.total_liters) }} L
                    </template>
                    <template v-slot:item.transaction_count="{ item }">
                      <v-chip small label color="accent" text-color="white">
                        {{ formatNumber(item.transaction_count) }}
                      </v-chip>
                    </template>
                    <template v-slot:item.has_app="{ item }">
                      <v-chip small :color="item.has_app ? 'success' : 'grey'" text-color="white">
                        <v-icon left x-small>{{ item.has_app ? 'mdi-cellphone' : 'mdi-credit-card-outline' }}</v-icon>
                        {{ item.has_app ? $t('App') : $t('Carte') }}
                      </v-chip>
                    </template>
                  </v-data-table>
                </v-card>
              </v-col>
            </v-row>
          </v-tab-item>

          <!-- ===== ONGLET 4 : RÉDEMPTIONS ===== -->
          <v-tab-item lazy>
            <v-row v-if="stats && stats.redemptions">
              <!-- KPIs rédemptions -->
              <v-col cols="12">
                <div class="stats-container" style="grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));">
                  <v-card class="stat-card stat-card-product" elevation="2" style="border: 2px solid #e8eaf6;">
                    <div class="d-flex align-center justify-space-between">
                      <div>
                        <div class="stat-label">{{ $t('Total demandes') }}</div>
                        <div class="stat-value" style="color: #7b1fa2; font-size: 24px;">{{ formatNumber(stats.redemptions.total) }}</div>
                      </div>
                      <div class="stat-icon" style="background: rgba(123, 31, 162, 0.12);">
                        <v-icon size="32" color="purple">mdi-hand-coin</v-icon>
                      </div>
                    </div>
                  </v-card>

                  <v-card class="stat-card stat-card-product" elevation="2" style="border: 2px solid #fff3e0;">
                    <div class="d-flex align-center justify-space-between">
                      <div>
                        <div class="stat-label">{{ $t('En attente') }}</div>
                        <div class="stat-value stat-value-warning" style="font-size: 24px;">{{ formatNumber(stats.redemptions.pending) }}</div>
                      </div>
                      <div class="stat-icon stat-icon-warning">
                        <v-icon size="32" color="orange">mdi-clock-alert</v-icon>
                      </div>
                    </div>
                  </v-card>

                  <v-card class="stat-card stat-card-product" elevation="2" style="border: 2px solid #e3f2fd;">
                    <div class="d-flex align-center justify-space-between">
                      <div>
                        <div class="stat-label">{{ $t('Approuvées') }}</div>
                        <div class="stat-value stat-value-primary" style="font-size: 24px;">{{ formatNumber(stats.redemptions.approved) }}</div>
                      </div>
                      <div class="stat-icon stat-icon-primary">
                        <v-icon size="32" color="primary">mdi-check-circle</v-icon>
                      </div>
                    </div>
                    <div class="stat-footer mt-1">
                      {{ formatNumber(stats.redemptions.approved_amount) }} XOF {{ $t('à encaisser') }}
                    </div>
                  </v-card>

                  <v-card class="stat-card stat-card-product" elevation="2" style="border: 2px solid #e8f5e9;">
                    <div class="d-flex align-center justify-space-between">
                      <div>
                        <div class="stat-label">{{ $t('Honorées') }}</div>
                        <div class="stat-value stat-value-success" style="font-size: 24px;">{{ formatNumber(stats.redemptions.fulfilled + stats.redemptions.paid) }}</div>
                      </div>
                      <div class="stat-icon stat-icon-success">
                        <v-icon size="32" color="success">mdi-check-decagram</v-icon>
                      </div>
                    </div>
                    <div class="stat-footer mt-1">
                      {{ formatNumber(stats.redemptions.fulfilled_amount) }} XOF {{ $t('encaissé') }}
                    </div>
                  </v-card>

                  <v-card class="stat-card stat-card-product" elevation="2" style="border: 2px solid #ffebee;">
                    <div class="d-flex align-center justify-space-between">
                      <div>
                        <div class="stat-label">{{ $t('Rejetées') }}</div>
                        <div class="stat-value" style="color: #f44336; font-size: 24px;">{{ formatNumber(stats.redemptions.rejected) }}</div>
                      </div>
                      <div class="stat-icon stat-icon-warning" style="background: rgba(244, 67, 54, 0.12);">
                        <v-icon size="32" color="error">mdi-close-circle</v-icon>
                      </div>
                    </div>
                  </v-card>
                </div>
              </v-col>

              <!-- Résumé montants -->
              <v-col cols="12">
                <v-card outlined class="pa-4 rounded-lg">
                  <div class="text-subtitle-1 font-weight-bold mb-4">
                    <v-icon left small color="primary">mdi-cash-multiple</v-icon>
                    {{ $t('Résumé financier des rédemptions') }}
                  </div>
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-card flat class="pa-4" style="background: #e8f5e9; border-radius: 12px;">
                        <div class="text-caption font-weight-bold text-uppercase" style="color: #2e7d32;">{{ $t('Montant total encaissé') }}</div>
                        <div class="text-h5 font-weight-bold" style="color: #1b5e20;">{{ formatNumber(stats.redemptions.fulfilled_amount) }} <span class="text-body-2">XOF</span></div>
                        <div class="text-caption mt-1">{{ stats.redemptions.fulfilled + stats.redemptions.paid }} {{ $t('demandes honorées') }}</div>
                      </v-card>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-card flat class="pa-4" style="background: #e3f2fd; border-radius: 12px;">
                        <div class="text-caption font-weight-bold text-uppercase" style="color: #1565c0;">{{ $t('Montant en attente d\'encaissement') }}</div>
                        <div class="text-h5 font-weight-bold" style="color: #0d47a1;">{{ formatNumber(stats.redemptions.approved_amount) }} <span class="text-body-2">XOF</span></div>
                        <div class="text-caption mt-1">{{ stats.redemptions.approved }} {{ $t('demandes approuvées') }}</div>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-card>
              </v-col>
            </v-row>
            <v-alert v-else type="info" outlined class="ma-4">
              {{ $t('Aucune donnée de rédemption disponible') }}
            </v-alert>
          </v-tab-item>

          <!-- ===== ONGLET 5 : INACTIFS ===== -->
          <v-tab-item lazy>
            <v-row v-if="stats && stats.users.inactive_users && stats.users.inactive_users.length">
              <!-- KPIs inactifs cliquables -->
              <v-col cols="12">
                <div class="stats-container" style="grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));">
                  <v-card
                    class="stat-card stat-card-product inactive-kpi-card"
                    :class="{ 'inactive-kpi-active': inactivePeriod === 7 }"
                    elevation="2"
                    style="border: 2px solid #fff3e0; cursor: pointer;"
                    @click="setInactivePeriod(7)"
                  >
                    <div class="d-flex align-center justify-space-between">
                      <div>
                        <div class="stat-label">{{ $t('Entre 7 et 15 jours') }}</div>
                        <div class="stat-value" style="color: #ff9800; font-size: 24px;">{{ formatNumber(stats.users.inactive_7_15) }}</div>
                      </div>
                      <div class="stat-icon" style="background: rgba(255, 152, 0, 0.12);">
                        <v-icon size="32" color="orange">mdi-account-clock</v-icon>
                      </div>
                    </div>
                    <div v-if="inactivePeriod === 7" class="inactive-kpi-indicator" style="background: #ff9800;"></div>
                  </v-card>
                  <v-card
                    class="stat-card stat-card-product inactive-kpi-card"
                    :class="{ 'inactive-kpi-active': inactivePeriod === 15 }"
                    elevation="2"
                    style="border: 2px solid #ffcdd2; cursor: pointer;"
                    @click="setInactivePeriod(15)"
                  >
                    <div class="d-flex align-center justify-space-between">
                      <div>
                        <div class="stat-label">{{ $t('Entre 16 et 30 jours') }}</div>
                        <div class="stat-value" style="color: #f44336; font-size: 24px;">{{ formatNumber(stats.users.inactive_16_30) }}</div>
                      </div>
                      <div class="stat-icon" style="background: rgba(244, 67, 54, 0.12);">
                        <v-icon size="32" color="error">mdi-account-alert</v-icon>
                      </div>
                    </div>
                    <div v-if="inactivePeriod === 15" class="inactive-kpi-indicator" style="background: #f44336;"></div>
                  </v-card>
                  <v-card
                    class="stat-card stat-card-product inactive-kpi-card"
                    :class="{ 'inactive-kpi-active': inactivePeriod === 30 }"
                    elevation="2"
                    style="border: 2px solid #ffebee; cursor: pointer;"
                    @click="setInactivePeriod(30)"
                  >
                    <div class="d-flex align-center justify-space-between">
                      <div>
                        <div class="stat-label">{{ $t('Plus de 30 jours') }}</div>
                        <div class="stat-value" style="color: #b71c1c; font-size: 24px;">{{ formatNumber(stats.users.inactive_30_plus) }}</div>
                      </div>
                      <div class="stat-icon" style="background: rgba(183, 28, 28, 0.12);">
                        <v-icon size="32" color="#b71c1c">mdi-account-remove</v-icon>
                      </div>
                    </div>
                    <div v-if="inactivePeriod === 30" class="inactive-kpi-indicator" style="background: #b71c1c;"></div>
                  </v-card>
                </div>
              </v-col>

              <!-- Liste des utilisateurs inactifs (datatable triable) -->
              <v-col cols="12">
                <v-card outlined class="rounded-lg">
                  <v-card-title class="d-flex align-center justify-space-between flex-wrap">
                    <div class="text-subtitle-1 font-weight-bold">
                      <v-icon left small color="error">mdi-account-remove</v-icon>
                      {{ $t('Utilisateurs inactifs') }} ({{ inactivePeriod }}+ {{ $t('jours') }})
                      <v-chip small color="error" text-color="white" class="ml-2">{{ filteredInactiveUsers.length }}</v-chip>
                    </div>
                    <v-btn small outlined color="success" @click="exportInactiveUsersExcel">
                      <v-icon small left>mdi-file-excel</v-icon> {{ $t('Exporter') }}
                    </v-btn>
                  </v-card-title>

                  <v-data-table
                    dense
                    :headers="inactiveTableHeaders"
                    :items="filteredInactiveUsers"
                    :items-per-page="25"
                    :footer-props="{ 'items-per-page-options': [15, 25, 50, 100, -1] }"
                    item-key="id"
                    class="bgTable"
                  >
                    <!-- Checkbox "traité" -->
                    <template v-slot:item.handled="{ item }">
                      <v-simple-checkbox
                        :value="!!contactedUsers[item.id]"
                        color="success"
                        @input="onToggleHandled(item)"
                      />
                    </template>

                    <!-- Utilisateur -->
                    <template v-slot:item.fullname="{ item }">
                      <div
                        class="d-flex align-center"
                        :class="{ 'text-decoration-line-through grey--text': contactedUsers[item.id] }"
                      >
                        <v-avatar size="32" color="error" class="mr-2">
                          <span class="white--text text-caption font-weight-bold">
                            {{ getInitials(item.fullname) }}
                          </span>
                        </v-avatar>
                        <div>
                          <div class="font-weight-bold capitalize">{{ item.fullname || 'N/A' }}</div>
                          <div class="text-caption grey--text">{{ item.phone || 'N/A' }}</div>
                        </div>
                      </div>
                    </template>

                    <template v-slot:item.origin_station="{ item }">
                      <span :class="{ 'text-decoration-line-through grey--text': contactedUsers[item.id] }">
                        {{ item.origin_station || '-' }}
                      </span>
                    </template>

                    <template v-slot:item.vehicle_type="{ item }">
                      <span :class="{ 'text-decoration-line-through grey--text': contactedUsers[item.id] }">
                        {{ item.vehicle_type || '-' }}
                      </span>
                    </template>

                    <template v-slot:item.has_app="{ item }">
                      <v-chip v-if="item.has_app" small color="success" text-color="white">
                        <v-icon left x-small>mdi-cellphone</v-icon>{{ $t('App') }}
                      </v-chip>
                      <v-chip v-else-if="item.has_card" small color="orange" text-color="white">
                        <v-icon left x-small>mdi-credit-card-outline</v-icon>{{ $t('Carte') }}
                      </v-chip>
                      <v-chip v-else small color="grey" text-color="white">
                        <v-icon left x-small>mdi-cellphone-off</v-icon>{{ $t('Non') }}
                      </v-chip>
                    </template>

                    <template v-slot:item.last_activity="{ item }">
                      <span v-if="item.last_activity">{{ formatDate(item.last_activity) }}</span>
                      <span v-else class="grey--text">{{ $t('Jamais') }}</span>
                    </template>

                    <template v-slot:item.days_since="{ item }">
                      <v-chip
                        small
                        :color="item.days_since > 30 ? '#b71c1c' : item.days_since > 15 ? 'error' : 'orange'"
                        text-color="white"
                      >
                        {{ item.days_since }} {{ $t('jours') }}
                      </v-chip>
                    </template>

                    <template v-slot:item.cause="{ item }">
                      <span v-if="item.cause" class="text-caption">{{ item.cause }}</span>
                      <span v-else class="grey--text text-caption">—</span>
                    </template>
                  </v-data-table>
                </v-card>
              </v-col>

              <!-- Dialog Marquer comme traité -->
              <v-dialog v-model="followupDialog.open" max-width="520" persistent>
                <v-card v-if="followupDialog.open">
                  <v-card-title class="success white--text">
                    <v-icon left dark>mdi-check-decagram</v-icon>
                    {{ $t('Marquer comme traité') }}
                    <v-spacer />
                    <v-btn icon dark @click="followupDialog.open = false">
                      <v-icon>mdi-close</v-icon>
                    </v-btn>
                  </v-card-title>
                  <v-card-text class="pt-4">
                    <div class="mb-3">
                      <div class="font-weight-bold">{{ followupDialog.item?.fullname }}</div>
                      <div class="text-caption grey--text">{{ followupDialog.item?.phone }}</div>
                    </div>
                    <v-combobox
                      v-model="followupDialog.cause"
                      :items="causeOptions"
                      :label="$t('Cause *')"
                      hint="Sélectionnez une cause existante ou saisissez-en une nouvelle"
                      persistent-hint
                      outlined
                      dense
                      clearable
                      prepend-inner-icon="mdi-help-circle-outline"
                      :rules="[v => !!v || $t('Cause requise')]"
                    />
                    <v-textarea
                      v-model="followupDialog.observation"
                      :label="$t('Observation')"
                      outlined
                      dense
                      rows="3"
                      auto-grow
                      prepend-inner-icon="mdi-text"
                      class="mt-2"
                    />
                  </v-card-text>
                  <v-divider />
                  <v-card-actions class="pa-3">
                    <v-spacer />
                    <v-btn text @click="followupDialog.open = false">
                      {{ $t('commons.cancel') }}
                    </v-btn>
                    <v-btn
                      color="success"
                      :loading="followupDialog.saving"
                      :disabled="!followupDialog.cause"
                      @click="submitFollowup"
                    >
                      <v-icon left>mdi-content-save</v-icon>
                      {{ $t('Enregistrer') }}
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </v-row>
            <v-alert v-else type="info" outlined class="ma-4">
              {{ $t('Aucun utilisateur inactif') }}
            </v-alert>
          </v-tab-item>
        </v-tabs-items>
      </div>

      <!-- Dialog utilisateurs uniques d'une station -->
      <v-dialog v-model="stationUsersDialog.open" max-width="800">
        <v-card v-if="stationUsersDialog.open">
          <v-card-title class="d-flex justify-space-between">
            <span>{{ $t('Utilisateurs') }} - {{ stationUsersDialog.stationName }}</span>
            <v-btn icon @click="stationUsersDialog.open = false"><v-icon>mdi-close</v-icon></v-btn>
          </v-card-title>
          <v-card-subtitle>
            {{ formatNumber(stationUsersDialog.users.length) }} {{ $t('utilisateurs uniques') }}
            <v-btn x-small outlined color="success" class="ml-2" @click="exportStationUsersList">
              <v-icon x-small left>mdi-microsoft-excel</v-icon>Excel
            </v-btn>
          </v-card-subtitle>
          <v-card-text style="max-height: 500px; overflow-y: auto;">
            <v-data-table
              dense
              :headers="stationUserHeaders"
              :items="stationUsersDialog.users"
              hide-default-footer
              disable-pagination
              :items-per-page="-1"
            >
              <template v-slot:item.fullname="{ item }">
                <span class="font-weight-bold">{{ item.fullname }}</span>
              </template>
              <template v-slot:item.has_app="{ item }">
                <v-chip x-small :color="item.has_app ? 'success' : 'grey'" text-color="white">
                  {{ item.has_app ? 'Mobile' : 'Carte' }}
                </v-chip>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-dialog>

      <!-- Overlays d'export -->
      <v-overlay :value="isExportingPdf" absolute opacity="0.9" z-index="9999">
        <div class="export-overlay-content">
          <v-progress-circular indeterminate size="64" width="6" color="error" />
          <div class="mt-4 font-weight-bold">{{ $t('Génération du PDF...') }}</div>
        </div>
      </v-overlay>

      <v-overlay :value="isExportingExcel" absolute opacity="0.9" z-index="9999">
        <div class="export-overlay-content">
          <v-progress-circular indeterminate size="64" width="6" color="success" />
          <div class="mt-4 font-weight-bold">{{ $t('Génération de l\'Excel...') }}</div>
        </div>
      </v-overlay>
    </template>
  </avs-card>
</template>

<script>
import FidelityStatsService from "@/services/FidelityStatsService"
import { ProductDetailService } from "@/services"
import ApexCharts from "apexcharts"
import * as XLSX from "xlsx"
import { saveAs } from "file-saver"
import jsPDF from "jspdf"
import autoTable from "jspdf-autotable"

export default {
  name: "FidelityStats",

  data() {
    const today = new Date()
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0)
    return {
      isLoading: false,
      isExportingPdf: false,
      isExportingExcel: false,
      stats: null,
      activeTab: 0,
      stationUsersDialog: { open: false, stationName: '', stationId: null, users: [] },
      stationUserHeaders: [
        { text: 'Utilisateur', value: 'fullname', sortable: true },
        { text: 'Telephone', value: 'phone', sortable: true },
        { text: 'Application', value: 'has_app', sortable: true, align: 'center' },
        { text: 'Montant (XOF)', value: 'total_amount', sortable: true },
        { text: 'Volume (L)', value: 'total_liters', sortable: true },
        { text: 'Transactions', value: 'transaction_count', sortable: true, align: 'center' },
      ],

      // Filtres
      startDate: firstDayOfMonth.toISOString().split("T")[0],
      endDate: lastDayOfMonth.toISOString().split("T")[0],
      menuStartDate: false,
      menuEndDate: false,
      selectedProduct: null,
      products: [],

      // Inactive users
      inactivePeriod: 30,
      inactiveDisplayCount: 30,
      contactedUsers: {},
      causeOptions: [],
      followupDialog: {
        open: false,
        item: null,
        cause: null,
        observation: "",
        saving: false,
      },

      // Charts
      monthlyChart: null,
      productChart: null,
      stationChart: null,
      appChart: null,

      // Headers tableaux
      productHeaders: [
        { text: this.$t("Produit"), value: "name", sortable: true },
        { text: this.$t("Montant (XOF)"), value: "total_amount", sortable: true },
        { text: this.$t("Volume (L)"), value: "total_liters", sortable: true },
        { text: this.$t("Transactions"), value: "transaction_count", sortable: true, align: "center" },
      ],
      stationHeaders: [
        { text: this.$t("Station"), value: "name", sortable: true },
        { text: this.$t("Montant (XOF)"), value: "total_amount", sortable: true },
        { text: this.$t("Volume (L)"), value: "total_liters", sortable: true },
        { text: this.$t("Transactions"), value: "transaction_count", sortable: true, align: "center" },
        { text: this.$t("Utilisateurs uniques"), value: "unique_users", sortable: true, align: "center" },
      ],
      userHeaders: [
        { text: this.$t("Utilisateur"), value: "fullname", sortable: true },
        { text: this.$t("Station d'origine"), value: "origin_station", sortable: true },
        { text: this.$t("Type vehicule"), value: "vehicle_type", sortable: true },
        { text: this.$t("Montant (XOF)"), value: "total_amount", sortable: true },
        { text: this.$t("Volume (L)"), value: "total_liters", sortable: true },
        { text: this.$t("Transactions"), value: "transaction_count", sortable: true, align: "center" },
        { text: this.$t("Application"), value: "has_app", sortable: true, align: "center" },
      ],
      inactiveUserHeaders: [
        { text: this.$t("Utilisateur"), value: "fullname", sortable: true },
        { text: this.$t("Application"), value: "has_app", sortable: true, align: "center" },
        { text: this.$t("Dernière activité"), value: "last_activity", sortable: true },
        { text: this.$t("Jours d'inactivité"), value: "days_since", sortable: true, align: "center" },
      ],
      inactiveTableHeaders: [
        { text: this.$t("Traité"), value: "handled", sortable: true, align: "center", width: 80 },
        { text: this.$t("Utilisateur"), value: "fullname", sortable: true },
        { text: this.$t("Station d'origine"), value: "origin_station", sortable: true },
        { text: this.$t("Type véhicule"), value: "vehicle_type", sortable: true },
        { text: this.$t("Application"), value: "has_app", sortable: true, align: "center" },
        { text: this.$t("Dernière activité"), value: "last_activity", sortable: true },
        { text: this.$t("Jours d'inactivité"), value: "days_since", sortable: true, align: "center" },
        { text: this.$t("Cause"), value: "cause", sortable: true },
      ],
    }
  },

  computed: {
    filteredInactiveUsers() {
      if (!this.stats || !this.stats.users.inactive_users) return []
      if (this.inactivePeriod === 7) {
        return this.stats.users.inactive_users.filter(u => u.days_since >= 7 && u.days_since <= 15)
      } else if (this.inactivePeriod === 15) {
        return this.stats.users.inactive_users.filter(u => u.days_since >= 16 && u.days_since <= 30)
      } else {
        return this.stats.users.inactive_users.filter(u => u.days_since > 30)
      }
    },
    visibleInactiveUsers() {
      return this.filteredInactiveUsers.slice(0, this.inactiveDisplayCount)
    },
  },

  created() {
    this.fetchProducts()
    this.fetchStats()
    this.fetchCauses()
  },

  beforeDestroy() {
    this.destroyCharts()
  },

  watch: {
    activeTab() {
      this.$nextTick(() => {
        this.renderCharts()
      })
    }
  },

  methods: {
    // ========== DATA FETCHING ==========

    async fetchStats() {
      this.isLoading = true
      try {
        const filters = {}
        if (this.startDate) filters.start_date = this.startDate
        if (this.endDate) filters.end_date = this.endDate
        if (this.selectedProduct) filters.product = this.selectedProduct

        this.stats = await FidelityStatsService.getStats(filters)

        // Hydrater l'etat "traite" a partir des flags renvoyes par le backend
        const inactive = (this.stats && this.stats.users && this.stats.users.inactive_users) || []
        const map = {}
        inactive.forEach(u => { if (u.handled) map[u.id] = true })
        this.contactedUsers = map

        this.$nextTick(() => {
          this.renderCharts()
        })
      } catch (e) {
        console.error("Erreur chargement statistiques", e)
        this.$store.commit("app/message", {
          msg: this.$t("messages.error.operation_failed"),
          color: "error",
        })
      } finally {
        this.isLoading = false
      }
    },

    fetchProducts() {
      ProductDetailService.getprincipalAll().then(response => {
        this.products = response.results
      })
    },

    applyFilters() {
      this.fetchStats()
    },

    // ========== CHARTS ==========

    destroyCharts() {
      if (this.monthlyChart) { this.monthlyChart.destroy(); this.monthlyChart = null }
      if (this.productChart) { this.productChart.destroy(); this.productChart = null }
      if (this.stationChart) { this.stationChart.destroy(); this.stationChart = null }
      if (this.appChart) { this.appChart.destroy(); this.appChart = null }
    },

    renderCharts() {
      if (!this.stats) return
      this.destroyCharts()

      if (this.activeTab === 0) {
        this.renderMonthlyChart()
        this.renderProductChart()
      } else if (this.activeTab === 1) {
        this.renderStationChart()
      } else if (this.activeTab === 2) {
        this.renderAppChart()
      }
    },

    renderMonthlyChart() {
      const el = this.$refs.monthlyChart
      if (!el || !this.stats.consumption.by_month.length) return

      const months = this.stats.consumption.by_month.map(m => m.month)
      const amounts = this.stats.consumption.by_month.map(m => parseFloat(m.total_amount) || 0)
      const liters = this.stats.consumption.by_month.map(m => parseFloat(m.total_liters) || 0)

      const isDark = this.$vuetify.theme.dark

      this.monthlyChart = new ApexCharts(el, {
        chart: { type: "area", height: 350, background: "transparent", toolbar: { show: true } },
        theme: { mode: isDark ? "dark" : "light" },
        series: [
          { name: "Montant (XOF)", data: amounts },
          { name: "Volume (L)", data: liters },
        ],
        xaxis: { categories: months, labels: { rotate: -45 } },
        yaxis: [
          { title: { text: "Montant (XOF)" }, labels: { formatter: v => this.formatNumber(Math.round(v)) } },
          { opposite: true, title: { text: "Volume (L)" }, labels: { formatter: v => this.formatNumber(Math.round(v)) } },
        ],
        colors: ["#1976d2", "#ff9800"],
        fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.5, opacityTo: 0.1, stops: [0, 90, 100] } },
        stroke: { curve: "smooth", width: 3 },
        dataLabels: { enabled: false },
        tooltip: { shared: true, intersect: false },
      })
      this.monthlyChart.render()
    },

    renderProductChart() {
      const el = this.$refs.productChart
      if (!el || !this.stats.consumption.by_product.length) return

      const isDark = this.$vuetify.theme.dark

      this.productChart = new ApexCharts(el, {
        chart: { type: "donut", height: 350, background: "transparent" },
        theme: { mode: isDark ? "dark" : "light" },
        series: this.stats.consumption.by_product.map(p => parseFloat(p.total_amount) || 0),
        labels: this.stats.consumption.by_product.map(p => p.name),
        colors: ["#1976d2", "#ff9800", "#4caf50", "#f44336", "#9c27b0", "#00bcd4"],
        legend: { position: "bottom", fontSize: "13px" },
        plotOptions: { pie: { donut: { size: "65%", labels: { show: true, total: { show: true, label: "Total", formatter: () => this.formatNumber(this.stats.consumption.total_amount) + " XOF" } } } } },
      })
      this.productChart.render()
    },

    renderStationChart() {
      const el = this.$refs.stationChart
      if (!el || !this.stats.consumption.by_station.length) return

      const isDark = this.$vuetify.theme.dark
      const top10 = this.stats.consumption.by_station.slice(0, 10)

      this.stationChart = new ApexCharts(el, {
        chart: { type: "bar", height: 400, background: "transparent", toolbar: { show: true } },
        theme: { mode: isDark ? "dark" : "light" },
        series: [
          { name: "Volume (L)", data: top10.map(s => parseFloat(s.total_liters) || 0) },
          { name: "Transactions", data: top10.map(s => s.transaction_count || 0) },
        ],
        xaxis: { categories: top10.map(s => s.name), labels: { rotate: -45, style: { fontSize: "11px" } } },
        plotOptions: { bar: { horizontal: false, columnWidth: "55%", borderRadius: 4 } },
        colors: ["#1976d2", "#ff9800"],
        dataLabels: { enabled: false },
        tooltip: { shared: true, intersect: false },
      })
      this.stationChart.render()
    },

    renderAppChart() {
      const el = this.$refs.appChart
      if (!el) return

      const isDark = this.$vuetify.theme.dark

      this.appChart = new ApexCharts(el, {
        chart: { type: "donut", height: 300, background: "transparent" },
        theme: { mode: isDark ? "dark" : "light" },
        series: [this.stats.users.with_app, this.stats.users.without_app],
        labels: [this.$t("App mobile"), this.$t("Carte physique")],
        colors: ["#4caf50", "#ff9800"],
        legend: { position: "bottom", fontSize: "14px" },
        plotOptions: { pie: { donut: { size: "60%", labels: { show: true, total: { show: true, label: "Total", formatter: () => this.formatNumber(this.stats.users.total) } } } } },
      })
      this.appChart.render()
    },

    // ========== EXPORTS ==========

    exportExcel() {
      if (!this.stats) return
      this.isExportingExcel = true

      try {
        const wb = XLSX.utils.book_new()

        // Feuille Produits
        const productRows = [["Produit", "Montant (XOF)", "Volume (L)", "Transactions"]]
        this.stats.consumption.by_product.forEach(p => {
          productRows.push([p.name, p.total_amount, p.total_liters, p.transaction_count])
        })
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(productRows), "Produits")

        // Feuille Stations
        const stationRows = [["Station", "Montant (XOF)", "Volume (L)", "Transactions", "Utilisateurs uniques"]]
        this.stats.consumption.by_station.forEach(s => {
          stationRows.push([s.name, s.total_amount, s.total_liters, s.transaction_count, s.unique_users])
        })
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(stationRows), "Stations")

        // Feuille Utilisateurs
        const userRows = [["Nom", "Téléphone", "Montant (XOF)", "Volume (L)", "Transactions", "Application"]]
        this.stats.consumption.top_users.forEach(u => {
          userRows.push([u.fullname, u.phone, u.total_amount, u.total_liters, u.transaction_count, u.has_app ? "Oui" : "Non"])
        })
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(userRows), "Utilisateurs")

        // Feuille Mensuel
        const monthRows = [["Mois", "Montant (XOF)", "Volume (L)", "Transactions", "Utilisateurs uniques"]]
        this.stats.consumption.by_month.forEach(m => {
          monthRows.push([m.month, m.total_amount, m.total_liters, m.transaction_count, m.unique_users])
        })
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(monthRows), "Mensuel")

        // Feuille Rédemptions
        if (this.stats.redemptions) {
          const r = this.stats.redemptions
          const redemptionRows = [
            ["Statistique", "Valeur"],
            ["Total demandes", r.total],
            ["En attente", r.pending],
            ["Approuvées", r.approved],
            ["Honorées (fuel)", r.fulfilled],
            ["Payées (cash)", r.paid],
            ["Rejetées", r.rejected],
            ["Montant encaissé (XOF)", r.fulfilled_amount],
            ["Montant à encaisser (XOF)", r.approved_amount],
          ]
          XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(redemptionRows), "Rédemptions")
        }

        // Feuille Inactifs
        const inactifRows = [
          ["Période d'inactivité", "Nombre d'utilisateurs"],
          ["Inactifs 7 jours", this.stats.users.inactive_7d],
          ["Inactifs 15 jours", this.stats.users.inactive_15d],
          ["Inactifs 30 jours", this.stats.users.inactive_30d],
        ]
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(inactifRows), "Inactifs")

        const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" })
        const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
        saveAs(blob, `statistiques_fidelite_${new Date().toISOString().slice(0, 10)}.xlsx`)

        this.$store.commit("app/message", { msg: this.$t("Téléchargé avec succès"), color: "success" })
      } catch (e) {
        console.error("Erreur export Excel:", e)
        this.$store.commit("app/message", { msg: this.$t("messages.error.operation_failed"), color: "error" })
      } finally {
        this.isExportingExcel = false
      }
    },

    exportPdf() {
      if (!this.stats) return
      this.isExportingPdf = true

      try {
        const doc = new jsPDF("landscape")
        const dateStr = new Date().toLocaleDateString("fr-FR")
        doc.setFontSize(16)
        doc.text(`Rapport Statistiques Fidélité - ${dateStr}`, 14, 15)

        // Résumé KPI
        doc.setFontSize(10)
        doc.text(`Utilisateurs: ${this.stats.users.total} | Avec app: ${this.stats.users.with_app} | Sans app: ${this.stats.users.without_app} | Inactifs 30j: ${this.stats.users.inactive_30d}`, 14, 25)
        doc.text(`Montant total: ${this.formatNumber(this.stats.consumption.total_amount)} XOF | Volume: ${this.formatNumber(this.stats.consumption.total_liters)} L`, 14, 32)

        // Tableau produits
        doc.setFontSize(12)
        doc.text("Détails par produit", 14, 42)
        autoTable(doc, {
          head: [["Produit", "Montant (XOF)", "Volume (L)", "Transactions"]],
          body: this.stats.consumption.by_product.map(p => [p.name, this.formatNumber(p.total_amount), this.formatNumber(p.total_liters), p.transaction_count]),
          startY: 46,
          styles: { fontSize: 8 },
          headStyles: { fillColor: [25, 118, 210] },
        })

        // Tableau stations
        const afterProducts = doc.lastAutoTable.finalY + 10
        doc.text("Performance par station", 14, afterProducts)
        autoTable(doc, {
          head: [["Station", "Montant (XOF)", "Volume (L)", "Transactions", "Utilisateurs"]],
          body: this.stats.consumption.by_station.map(s => [s.name, this.formatNumber(s.total_amount), this.formatNumber(s.total_liters), s.transaction_count, s.unique_users]),
          startY: afterProducts + 4,
          styles: { fontSize: 8 },
          headStyles: { fillColor: [25, 118, 210] },
        })

        // Tableau top utilisateurs
        doc.addPage()
        doc.setFontSize(12)
        doc.text("Top utilisateurs", 14, 15)
        autoTable(doc, {
          head: [["Nom", "Téléphone", "Montant (XOF)", "Volume (L)", "Transactions", "App"]],
          body: this.stats.consumption.top_users.map(u => [u.fullname || "N/A", u.phone || "N/A", this.formatNumber(u.total_amount), this.formatNumber(u.total_liters), u.transaction_count, u.has_app ? "Oui" : "Non"]),
          startY: 20,
          styles: { fontSize: 8 },
          headStyles: { fillColor: [25, 118, 210] },
        })

        // Utilisateurs inactifs
        const afterUsers = doc.lastAutoTable.finalY + 10
        doc.text("Utilisateurs inactifs", 14, afterUsers)
        autoTable(doc, {
          head: [["Période", "Nombre"]],
          body: [
            ["Inactifs 7 jours", this.stats.users.inactive_7d],
            ["Inactifs 15 jours", this.stats.users.inactive_15d],
            ["Inactifs 30 jours", this.stats.users.inactive_30d],
          ],
          startY: afterUsers + 4,
          styles: { fontSize: 8 },
          headStyles: { fillColor: [183, 28, 28] },
        })

        // Rédemptions
        if (this.stats.redemptions) {
          const r = this.stats.redemptions
          const afterInactive = doc.lastAutoTable.finalY + 10
          doc.text("Demandes de rédemption", 14, afterInactive)
          autoTable(doc, {
            head: [["Statistique", "Valeur"]],
            body: [
              ["Total demandes", r.total],
              ["En attente", r.pending],
              ["Approuvées", r.approved],
              ["Honorées", r.fulfilled + r.paid],
              ["Rejetées", r.rejected],
              ["Montant encaissé", this.formatNumber(r.fulfilled_amount) + " XOF"],
              ["Montant à encaisser", this.formatNumber(r.approved_amount) + " XOF"],
            ],
            startY: afterInactive + 4,
            styles: { fontSize: 8 },
            headStyles: { fillColor: [123, 31, 162] },
          })
        }

        doc.save(`statistiques_fidelite_${new Date().toISOString().slice(0, 10)}.pdf`)
        this.$store.commit("app/message", { msg: this.$t("Téléchargé avec succès"), color: "success" })
      } catch (e) {
        console.error("Erreur export PDF:", e)
        this.$store.commit("app/message", { msg: this.$t("messages.error.operation_failed"), color: "error" })
      } finally {
        this.isExportingPdf = false
      }
    },

    exportStationsExcel() {
      if (!this.stats) return
      const rows = [["Station", "Montant (XOF)", "Volume (L)", "Transactions", "Utilisateurs uniques"]]
      this.stats.consumption.by_station.forEach(s => {
        rows.push([s.name, s.total_amount, s.total_liters, s.transaction_count, s.unique_users])
      })
      const ws = XLSX.utils.aoa_to_sheet(rows)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, "Stations")
      const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" })
      saveAs(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `stations_fidelite_${new Date().toISOString().slice(0, 10)}.xlsx`)
    },

    exportInactiveUsersExcel() {
      if (!this.filteredInactiveUsers.length) return
      const rows = [["Nom", "Telephone", "Station d'origine", "Type vehicule", "Application", "Derniere activite", "Jours d'inactivite"]]
      this.filteredInactiveUsers.forEach(u => {
        rows.push([
          u.fullname,
          u.phone,
          u.origin_station || '',
          u.vehicle_type || '',
          u.has_app ? "Oui" : "Non",
          u.last_activity ? this.formatDate(u.last_activity) : "Jamais",
          u.days_since,
        ])
      })
      const ws = XLSX.utils.aoa_to_sheet(rows)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, "Inactifs")
      const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" })
      saveAs(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `utilisateurs_inactifs_${new Date().toISOString().slice(0, 10)}.xlsx`)
    },

    openStationUsers(station) {
      if (!this.stats || !this.stats.consumption) return
      // Recuperer les top_users filtres par cette station via le queryset
      // On utilise les top_users existants et on filtre cote client
      // Pour avoir la liste complete, on fait un appel API avec le filtre station
      this.stationUsersDialog.stationName = station.name
      this.stationUsersDialog.stationId = station.id
      this.stationUsersDialog.open = true
      // Charger les users de cette station
      this.loadStationUsers(station.id)
    },

    async loadStationUsers(stationId) {
      try {
        const filters = {
          start_date: this.startDate,
          end_date: this.endDate,
          station: stationId,
        }
        if (this.selectedProduct) filters.product = this.selectedProduct
        const data = await FidelityStatsService.getStats(filters)
        this.stationUsersDialog.users = data.consumption?.top_users || []
      } catch (e) {
        console.error("Erreur chargement users station", e)
      }
    },

    exportStationUsersList() {
      if (!this.stationUsersDialog.users.length) return
      const rows = [["Nom", "Telephone", "Application", "Montant (XOF)", "Volume (L)", "Transactions"]]
      this.stationUsersDialog.users.forEach(u => {
        rows.push([
          u.fullname,
          u.phone,
          u.has_app ? "Oui" : "Non",
          u.total_amount,
          u.total_liters,
          u.transaction_count,
        ])
      })
      const ws = XLSX.utils.aoa_to_sheet(rows)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, "Utilisateurs station")
      const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" })
      saveAs(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `users_${this.stationUsersDialog.stationName}_${new Date().toISOString().slice(0, 10)}.xlsx`)
    },

    exportUsersExcel() {
      if (!this.stats) return
      const rows = [["Nom", "Telephone", "Station d'origine", "Type vehicule", "Montant (XOF)", "Volume (L)", "Transactions", "Application"]]
      this.stats.consumption.top_users.forEach(u => {
        rows.push([u.fullname, u.phone, u.origin_station || '', u.vehicle_type || '', u.total_amount, u.total_liters, u.transaction_count, u.has_app ? "Oui" : "Non"])
      })
      const ws = XLSX.utils.aoa_to_sheet(rows)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, "Utilisateurs")
      const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" })
      saveAs(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `utilisateurs_fidelite_${new Date().toISOString().slice(0, 10)}.xlsx`)
    },

    // ========== INACTIVE USERS ==========

    async fetchCauses() {
      try {
        this.causeOptions = await FidelityStatsService.getInactiveFollowupCauses()
      } catch (e) {
        this.causeOptions = []
      }
    },

    onToggleHandled(item) {
      const isChecked = !!this.contactedUsers[item.id]
      if (!isChecked) {
        // On demande cause + observation avant de rayer
        this.followupDialog = {
          open: true,
          item,
          cause: item.cause || null,
          observation: item.observation || "",
          saving: false,
        }
      } else {
        this.unmarkContacted(item)
      }
    },

    async submitFollowup() {
      const { item, cause, observation } = this.followupDialog
      if (!item || !cause) return
      this.followupDialog.saving = true
      try {
        const payload = {
          user_id: item.id,
          handled: true,
          cause: typeof cause === "string" ? cause : (cause?.text || ""),
          observation: observation || "",
        }
        const res = await FidelityStatsService.markInactiveFollowup(payload)
        this.$set(this.contactedUsers, item.id, true)
        // Mettre a jour l'item local (pour la colonne Cause et l'export)
        item.handled = true
        item.cause = res.cause
        item.observation = res.observation
        item.handled_at = res.handled_at
        // Ajouter la cause aux options si nouvelle
        if (payload.cause && !this.causeOptions.includes(payload.cause)) {
          this.causeOptions.push(payload.cause)
          this.causeOptions.sort()
        }
        this.followupDialog.open = false
        this.$store.commit("app/message", {
          msg: this.$t("Utilisateur marqué comme traité"),
          color: "success",
        })
      } catch (e) {
        this.$store.commit("app/message", {
          msg: this.$t("messages.error.operation_failed"),
          color: "error",
        })
      } finally {
        this.followupDialog.saving = false
      }
    },

    async unmarkContacted(item) {
      try {
        await FidelityStatsService.markInactiveFollowup({
          user_id: item.id,
          handled: false,
        })
        this.$set(this.contactedUsers, item.id, false)
        item.handled = false
        item.cause = null
        item.observation = null
        item.handled_at = null
      } catch (e) {
        this.$store.commit("app/message", {
          msg: this.$t("messages.error.operation_failed"),
          color: "error",
        })
      }
    },

    setInactivePeriod(days) {
      this.inactivePeriod = days
      this.inactiveDisplayCount = 30
      // Scroll back to top
      if (this.$refs.inactiveScrollContainer) {
        this.$refs.inactiveScrollContainer.scrollTop = 0
      }
    },

    onInactiveScroll(e) {
      const container = e.target
      const threshold = 100
      if (container.scrollTop + container.clientHeight >= container.scrollHeight - threshold) {
        if (this.visibleInactiveUsers.length < this.filteredInactiveUsers.length) {
          this.inactiveDisplayCount += 30
        }
      }
    },

    // ========== UTILITAIRES ==========

    formatNumber(value) {
      if (!value && value !== 0) return "0"
      return Math.round(Number(value)).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ")
    },

    formatDate(datetime) {
      if (!datetime) return '-'
      const d = new Date(datetime)
      return d.toLocaleDateString('fr-FR', { year: 'numeric', month: 'short', day: 'numeric' })
    },

    getInitials(fullname) {
      if (!fullname) return "U"
      const parts = fullname.trim().split(" ").filter(Boolean)
      if (parts.length === 0) return "U"
      if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
    },
  },
}
</script>

<style scoped>
.fidelity-stats-container {
  border-radius: 16px;
  overflow: hidden;
}

/* ===== Section stats KPI ===== */
.stats-section {
  margin-top: 10px;
  margin-bottom: 0;
  padding: 5px 0;
  background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%);
  border-radius: 16px;
}
.stats-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
  padding: 0 5px;
}

/* Carte principale */
.stat-card-main {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%) !important;
  border-radius: 20px !important;
  padding: 16px 24px;
  color: white;
  box-shadow: 0 10px 30px rgba(25, 118, 210, 0.3) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  position: relative;
  overflow: hidden;
}
.stat-card-main::before {
  content: "";
  position: absolute;
  top: -50%;
  right: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
  pointer-events: none;
}
.stat-card-main:hover {
  transform: translateY(-6px);
  box-shadow: 0 15px 40px rgba(25, 118, 210, 0.4) !important;
}
.stat-card-main .stat-secondary {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
}

/* Cartes secondaires */
.stat-card-product {
  border-radius: 16px !important;
  padding: 16px 20px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 2px solid #e3f2fd;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08) !important;
}
.stat-card-product:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12) !important;
  border-color: #90caf9;
}

/* Labels et valeurs */
.stat-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 700;
  margin-bottom: 6px;
}
.stat-value {
  font-size: 28px;
  font-weight: 800;
  line-height: 1.2;
}
.stat-unit {
  font-size: 16px;
  font-weight: 600;
  opacity: 0.85;
}
.stat-value-success { color: #4caf50; }
.stat-value-primary { color: #1976d2; }
.stat-value-warning { color: #ff9800; }
.stat-icon {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
}
.stat-icon-success { background: rgba(76, 175, 80, 0.12); }
.stat-icon-primary { background: rgba(25, 118, 210, 0.12); }
.stat-icon-warning { background: rgba(255, 152, 0, 0.12); }
.stat-footer {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  padding-top: 12px;
  border-top: 1px solid #e0e0e0;
}

/* Export overlay */
.export-overlay-content {
  text-align: center;
  padding: 32px;
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  color: #212121;
}

/* ===== Inactive KPI clickable cards ===== */
.inactive-kpi-card {
  position: relative;
  transition: all 0.3s ease;
}
.inactive-kpi-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
}
.inactive-kpi-active {
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.18) !important;
  transform: translateY(-2px);
}
.inactive-kpi-indicator {
  position: absolute;
  bottom: 0;
  left: 10%;
  right: 10%;
  height: 4px;
  border-radius: 4px 4px 0 0;
}

/* ===== Infinite scroll list ===== */
.inactive-users-scroll-list {
  max-height: 600px;
  overflow-y: auto;
  scrollbar-width: thin;
}
.inactive-users-scroll-list::-webkit-scrollbar {
  width: 6px;
}
.inactive-users-scroll-list::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}
.inactive-list-header {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  background: #f5f5f5;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #616161;
  border-bottom: 2px solid #e0e0e0;
  position: sticky;
  top: 0;
  z-index: 1;
}
.inactive-list-row {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.15s ease;
}
.inactive-list-row:hover {
  background: #fafafa;
}
.inactive-col {
  display: flex;
  align-items: center;
}
.inactive-col-check {
  flex: 0 0 40px;
  justify-content: center;
  min-width: 40px;
}
.inactive-contacted {
  opacity: 0.5;
  text-decoration: line-through;
  background: rgba(76, 175, 80, 0.05);
}
.inactive-col-user {
  flex: 2;
  min-width: 180px;
}
.inactive-col-station {
  flex: 1;
  min-width: 120px;
}
.inactive-col-vtype {
  flex: 1;
  min-width: 100px;
}
.inactive-col-app {
  flex: 0.8;
  justify-content: center;
  min-width: 90px;
}
.inactive-col-date {
  flex: 1;
  min-width: 120px;
}
.inactive-col-days {
  flex: 0.8;
  justify-content: center;
  min-width: 100px;
}

.capitalize {
  text-transform: capitalize;
}

/* Responsive */
@media (max-width: 960px) {
  .stats-container {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (max-width: 600px) {
  .stats-container {
    grid-template-columns: 1fr;
  }
  .stat-value {
    font-size: 24px;
  }
}
</style>
